// screens/GuidedPhotoScreen.js - Screen 4: Fixed 6-step guided photo flow
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useLang } from '../contexts/LanguageContext';
import { ConfirmDialog } from '../components/shared';
import api, { getErrorMessage } from '../utils/api';

// Capture order requested by the client: Label → Full set → Front → Left → Back → Right.
const BASE_STEPS = [
  { name: 'Production Label', icon: '🏷', description: 'Photograph the production label clearly' },
  { name: 'Full Set Overview', icon: '⊞', description: 'Photograph all items together' },
  { name: 'Front View', icon: '⬛', description: 'Photograph the front of the product' },
  { name: 'Left Side', icon: '◧', description: 'Photograph the left side' },
  { name: 'Back View', icon: '⬚', description: 'Photograph the back of the product' },
  { name: 'Right Side', icon: '◨', description: 'Photograph the right side' },
];

export default function GuidedPhotoScreen() {
  const { orderId, sessionId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { t } = useLang();

  const [steps, setSteps] = useState(BASE_STEPS);
  const [currentStep, setCurrentStep] = useState(0);
  const [photos, setPhotos] = useState(Array(BASE_STEPS.length).fill(null)); // null = not taken, {data, file} = taken, 'na' = N/A

  // Extra photos beyond the fixed steps — appended on demand by the photographer.
  const addExtraPhoto = () => {
    setSteps((s) => [...s, { name: `Extra ${s.length - BASE_STEPS.length + 1}`, icon: '➕', description: 'Additional photo for this product' }]);
    setPhotos((p) => [...p, null]);
    setCurrentStep(steps.length);
  };
  const [loading, setLoading] = useState(false);
  const [showRestartConfirm, setShowRestartConfirm] = useState(false);
  const [showNAConfirm, setShowNAConfirm] = useState(false);
  // Seed from navigation state so offline (locally-created) orders still show
  // their number/product before any server fetch.
  const [orderInfo, setOrderInfo] = useState(
    location.state?.order_number
      ? { order_number: location.state.order_number, product_name: location.state.product_name }
      : null
  );
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef();

  useEffect(() => {
    loadOrder();
  }, [orderId]);

  const loadOrder = async () => {
    try {
      const { data } = await api.get(`/api/orders/${orderId}`);
      setOrderInfo(data.order);
    } catch {}
  };

  const step = steps[currentStep];
  const stepPhoto = photos[currentStep];
  const allDone = photos.every(p => p !== null);
  const deviceId = navigator.userAgent.slice(0, 40);

  const handleCapture = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setPhotos((prev) => {
        const newPhotos = [...prev];
        newPhotos[currentStep] = { data: ev.target.result, file, step: steps[currentStep].name };
        return newPhotos;
      });
      // Fewer taps: once a shot is confirmed, jump straight to the next step so the
      // photographer only taps "capture" again instead of next + capture.
      setCurrentStep((s) => (s < steps.length - 1 ? s + 1 : s));
    };
    reader.readAsDataURL(file);
    // Reset input
    e.target.value = '';
  };

  const handleRetake = () => {
    fileInputRef.current?.click();
  };

  const handleNA = () => setShowNAConfirm(true);

  const confirmNA = () => {
    const newPhotos = [...photos];
    newPhotos[currentStep] = 'na';
    setPhotos(newPhotos);
    setShowNAConfirm(false);
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) setCurrentStep(s => s + 1);
    else navigate(`/session-summary/${orderId}/${sessionId}`, { state: { photos, steps: steps } });
  };

  const handlePrev = () => {
    if (currentStep > 0) setCurrentStep(s => s - 1);
  };

  const handleRestart = () => setShowRestartConfirm(true);

  const confirmRestart = async () => {
    setSteps(BASE_STEPS);
    setPhotos(Array(BASE_STEPS.length).fill(null));
    setCurrentStep(0);
    setShowRestartConfirm(false);
    toast.info(t('guided.restarted'));
  };

  // Progress indicator
  const getStepStatus = (idx) => {
    if (photos[idx] === 'na') return 'na';
    if (photos[idx] !== null) return 'completed';
    if (idx === currentStep) return 'active';
    return 'default';
  };

  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      height: '100vh', height: '100dvh',
      background: 'var(--bg-primary)', overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 16px',
        background: 'var(--bg-secondary)',
        borderBottom: '1px solid var(--border-subtle)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between'
      }}>
        <button className="btn btn-ghost btn-icon btn-sm" onClick={() => navigate(-1)} style={{ minHeight: 44 }}>←</button>
        <div style={{ textAlign: 'center' }}>
          {orderInfo && (
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
              {orderInfo.order_number}
            </div>
          )}
          <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
            {orderInfo?.product_name}
          </div>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={handleRestart}
          style={{ color: 'var(--accent-warning)', minHeight: 44, fontSize: '0.8125rem' }}>
          ↺ {t('guided.restart')}
        </button>
      </div>

      {/* Progress bar */}
      <div style={{ padding: '10px 16px', background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border-subtle)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{t('guided.step')} {currentStep + 1} {t('guided.of')} {steps.length}</span>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            {photos.filter(p => p !== null).length} {t('guided.captured')}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div className="step-progress" style={{ flex: 1 }}>
            {steps.map((_, i) => (
              <div key={i} className={`step-dot ${getStepStatus(i)}`}
                onClick={() => setCurrentStep(i)}
                style={{ cursor: 'pointer' }} />
            ))}
          </div>
          <button className="btn btn-secondary btn-sm" onClick={addExtraPhoto}
            title="Add an extra photo beyond the standard steps"
            style={{ flexShrink: 0, whiteSpace: 'nowrap', fontWeight: 600,
              border: '1px solid var(--accent-primary)', color: 'var(--accent-primary)' }}>
            ＋ {t('guided.extra')}
          </button>
        </div>
      </div>

      {/* Main content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '16px', gap: 16, overflow: 'hidden' }}>

        {/* Step name */}
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1rem', marginBottom: 4 }}>{step.icon}</div>
          <div className="guided-step-name">{step.name}</div>
          <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginTop: 4 }}>{step.description}</div>
        </div>

        {/* Photo area */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {stepPhoto === 'na' ? (
            <div style={{
              flex: 1, background: 'var(--bg-tertiary)',
              borderRadius: 'var(--radius-lg)', border: '2px dashed var(--border-normal)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              gap: 12, cursor: 'pointer', minHeight: 200
            }} onClick={handleRetake}>
              <div style={{ fontSize: '2rem', color: 'var(--text-muted)' }}>🚫</div>
              <div style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>{t('guided.namarked')}</div>
              <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>{t('guided.tapinstead')}</div>
            </div>
          ) : stepPhoto ? (
            <div style={{
              flex: 1, position: 'relative', borderRadius: 'var(--radius-lg)', overflow: 'hidden',
              minHeight: 200, background: 'var(--bg-tertiary)'
            }}>
              <img src={stepPhoto.data} alt={step.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              <div style={{
                position: 'absolute', bottom: 12, left: 12, right: 12,
                display: 'flex', gap: 8, justifyContent: 'center'
              }}>
                <button className="btn btn-secondary btn-sm" onClick={handleRetake}>
                  📷 {t('guided.retake')}
                </button>
              </div>
              {/* Success indicator */}
              <div style={{
                position: 'absolute', top: 12, right: 12,
                background: 'var(--accent-success)', borderRadius: '50%',
                width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'white', fontWeight: 700, fontSize: '1rem'
              }}>✓</div>
            </div>
          ) : (
            <div className="camera-preview-area" onClick={() => fileInputRef.current?.click()}>
              <div className="camera-icon">📷</div>
              <div style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>{t('guided.taptotake')}</div>
              <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>{t('guided.orientation')}</div>
            </div>
          )}
        </div>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleCapture}
          style={{ display: 'none' }}
        />
      </div>

      {/* Bottom controls */}
      <div style={{
        padding: '12px 16px',
        background: 'var(--bg-secondary)',
        borderTop: '1px solid var(--border-subtle)',
        display: 'grid',
        gridTemplateColumns: '1fr auto 1fr',
        gap: 8, alignItems: 'center'
      }}>
        <button
          className="btn btn-secondary"
          onClick={handlePrev}
          disabled={currentStep === 0}
          style={{ minHeight: 52 }}
        >
          ← {t('guided.prev')}
        </button>

        <button
          className="btn btn-ghost btn-sm"
          onClick={handleNA}
          style={{ color: 'var(--text-muted)', minHeight: 52, whiteSpace: 'nowrap' }}
        >
          {t('guided.na')}
        </button>

        <button
          className={`btn ${stepPhoto ? 'btn-primary' : 'btn-secondary'}`}
          onClick={stepPhoto ? handleNext : () => fileInputRef.current?.click()}
          style={{ minHeight: 52, fontWeight: 600 }}
        >
          {!stepPhoto ? `${t('guided.capture')} →` :
           currentStep === steps.length - 1 ? `${t('guided.review')} ✓` : `${t('guided.next')} →`}
        </button>
      </div>

      {/* Dialogs */}
      <ConfirmDialog
        open={showRestartConfirm}
        title={t('guided.restart.title')}
        message={t('guided.restart.msg')}
        confirmLabel={t('guided.restart.yes')}
        danger
        onConfirm={confirmRestart}
        onCancel={() => setShowRestartConfirm(false)}
      />
      <ConfirmDialog
        open={showNAConfirm}
        title={`Mark "${step.name}" as N/A?`}
        message="This step will be skipped. You can come back and take the photo later."
        confirmLabel="Mark N/A"
        onConfirm={confirmNA}
        onCancel={() => setShowNAConfirm(false)}
      />
    </div>
  );
}
